import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { App as CapacitorApp } from '@capacitor/app';

import { computeHasFeature, useAuthStore } from '@/stores/auth';
import { authApi, type FeatureKey, type Role } from '@/api/auth';
import { setLanguage } from '@/app/i18n';
import { useAndroidBack } from '@/hooks/useAndroidBack';
import { useSwipeBackRoots } from '@/lib/nativeNav';
import { tapHeavy, tapSelection } from '@/lib/haptics';
import { isFeatureOn } from '@/lib/appConfig';
import { cn } from '@/lib/utils';
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';
import { STORE_TRAINER_KEYS, storeTabs, trainersTabEmpty } from '@/lib/storeTabs';
import {
  aiSkillsFailed,
  aiSkillsLoaded,
  ensureAiSkills,
  getAiSkills,
  getAiSkillsVersion,
  subscribeAiSkills,
} from '@/lib/aiSkills';
import { markUserLogout } from '@/lib/loginReturn';
import { userSections } from '@/lib/studentFields';
import { MORE_PATH, isUnderMore, splitStaffNav } from '@/lib/staffMore';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { ShellContext } from '@/components/layout/shellContext';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { NavBadge } from '@/components/layout/NavBadge';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { usePushSync } from '@/hooks/usePushSync';
import { disablePush } from '@/lib/push';
import { OfflineBar } from '@/components/layout/OfflineBar';
import { AudioNotice } from '@/components/ui/AudioNotice';
import { RecordingBar } from '@/components/lesson/RecordingBar';
import { BiometricPrompt } from '@/components/BiometricPrompt';
import { dockLabelNoWrap } from '@/lib/dockLabel';

type NavItem = {
  to: string;
  labelKey: string;
  icon: string; // Material Symbol name
  /**
   * Tariff feature(s) this entry requires. When an array, the entry is shown if
   * the student has ANY of them. Undefined = core entry, never gated.
   */
  feature?: FeatureKey | FeatureKey[];
  /** Цифра у значка: чего непрочитано. Пока одна — уведомления куратора. */
  badge?: 'notifications';
};

/**
 * Разделы, которые живут только вместе с живым видео. Пока видео выключено
 * (см. настройку video), их не должно быть в меню: кнопка, ведущая в
 * неработающую комнату, читается как поломка платформы.
 */
/**
 * Разговорные комнаты закрываются переключателем `speaking_peers`.
 *
 * ⚠️ РАНЬШЕ ЗДЕСЬ БЫЛ `video`, И ЭТО БЫЛО НЕВЕРНО. Комнаты голосовые — камеры в
 * них нет вовсе, — а переключатель «видео» относится к живым видеоурокам, и он
 * выключен. Из-за этого пункт у куратора не показывался никогда, и со стороны
 * выглядело, будто раздела нет: заказчик так его и не нашёл (05.09.2026).
 *
 * ⚠️ Тот же ключ отбирает комнаты в расписании ученика — держим ОДИН
 * переключатель на одну возможность, иначе комнату видно в одном месте и не
 * видно в другом.
 */
const SPEAKING_ROOM_NAV_PATHS = new Set(['/curator/speaking-rooms', '/admin/speaking-rooms']);

/**
 * Пунктов кабинетов «только сайт» больше нет: сторис с 1.1.2 есть и в приложении (сторис 2.0, 13.10.2026, D61
 * снят), а пункта «Тарифы» нет нигде с 13.10.2026 — раздел убран целиком (решение владельца). Прежний список
 * `STORE_ABSENT_NAV_PATHS` (26.09.2026, сборка 61) удалён вместе с ними.
 */

/**
 * Пункты только для директора (admin): деньги и метрики, а также корзина
 * удалённых учёток — удалять и восстанавливать может только admin (F6).
 * И движок ИИ-заданий (D27): одобренное сразу уходит ученикам, решение
 * владельца 11.09 — только admin; ручки службы менеджера тоже не пускают.
 * И тексты уведомлений (D45): правка видна людям сразу, без выката, и уходит
 * полутора тысячам человек разом — ручки службы тоже только admin.
 */
const FINANCIAL_NAV_PATHS = new Set([
  '/admin/stats',
  '/admin/trash',
  '/admin/ai-pipeline',
  '/admin/notification-templates',
]);

/** Shell variants — the manager reuses the admin shell, gated by user role. */
type ShellRole = 'student' | 'curator' | 'admin';

const NAV_BY_ROLE: Record<ShellRole, NavItem[]> = {
  student: [
    { to: '/app', labelKey: 'nav.dashboard', icon: 'home' },
    // «Обучение» = Уроки + Домашка + Рейтинг в одном хабе с вкладками
    // (/app/lessons). Отдельные роуты /app/homework и /app/leaderboard живут для
    // прямых ссылок (напр. сдача ДЗ из плеера).
    { to: '/app/lessons', labelKey: 'nav.learning', icon: 'school' },
    /**
     * Календарь. ⚠️ ОТКРЫВАЕТСЯ И ПО `speaking_peers`, А НЕ ТОЛЬКО ПО `live`.
     *
     * Разговорные комнаты показываются ученику ТОЛЬКО здесь и в виджете на
     * главной — отдельного раздела у него нет намеренно (решение заказчика
     * 05.09.2026). А раз попасть в комнату можно лишь отсюда, закрывать
     * календарь ключом видеоуроков нельзя: ученик записался бы в комнату и не
     * имел ни одной кнопки, чтобы в неё войти. Так и было — проверено на
     * demo@onebilim.kz, у которого в тарифе один `lessons`.
     */
    // ⚠️ БЕЗ УСЛОВИЯ ПО ТАРИФУ. Пункт пропадал у того, у кого нет ни живых
    // уроков, ни разговорных комнат, — а календарь при этом полон его
    // собственных событий: задания на день, вебинары, личные заметки.
    // Заказчик 07.09.2026: «не понял прикола, зачем заблокирован сам раздел
    // расписания».
    { to: '/app/schedule', labelKey: 'nav.schedule', icon: 'event' },
    // Speaking Rooms убраны из меню ученика — они и так видны в Кесте
    // (расписании). Роут /app/speaking-rooms остаётся рабочим.
    // Словарь = personal dictionary + the "Учить слова" trainer (cue-cards is
    // now folded in here, reached from the vocabulary page's hero card).
    { to: '/app/vocabulary', labelKey: 'nav.vocabulary', icon: 'style', feature: 'vocab' },
    // Single "AI Trainers" entry — all trainers live under the hub, gated by
    // tariff inside it (Edvibe-style consolidation). Individual routes stay
    // reachable for backward compatibility.
    // Тренажёры = AI-тренажёры + Мок-тест (мок-карточка живёт внутри хаба).
    //
    // ⚠️ ПУНКТ ЗАКРЫВАЕТСЯ ВСЕМИ ШЕСТЬЮ КЛЮЧАМИ СРАЗУ (04.09.2026). До этого он
    // не был закрыт ничем: тариф без единого тренажёра всё равно показывал
    // раздел, а внутри ученика ждал пустой экран. Заказчик: «у них этот раздел
    // вообще не должен показываться, потому что у них нет этого тренажёра».
    {
      to: '/app/trainers',
      labelKey: 'nav.trainers',
      icon: 'smart_toy',
      feature: [...STORE_TRAINER_KEYS],
    },
    { to: '/app/chat', labelKey: 'nav.chat', icon: 'chat', feature: 'chat' },
    { to: '/app/profile', labelKey: 'nav.profile', icon: 'person' },
  ],
  curator: [
    { to: '/curator', labelKey: 'nav.dashboard', icon: 'home' },
    { to: '/curator/students', labelKey: 'nav.students', icon: 'groups' },
    { to: '/curator/homework', labelKey: 'nav.homework', icon: 'assignment' },
    // Порядок здесь — порядок БОКОВОГО меню. Нижнее меню телефона с 13.10.2026 собирается не из первых пунктов, а
    // по `lib/staffMore.ts` (4 пункта + «Ещё»); до этого «Уведомления» и «Группы» держали 4-е и 6-е места ради него.
    { to: '/curator/notifications', labelKey: 'nav.notifications', icon: 'notifications', badge: 'notifications' },
    { to: '/curator/chat', labelKey: 'nav.chat', icon: 'chat' },
    // Группы по подтипам (D40).
    { to: '/curator/groups', labelKey: 'nav.groups', icon: 'group' },
    { to: '/curator/flagged-works', labelKey: 'nav.flaggedWorks', icon: 'flag' },
    { to: '/curator/schedule', labelKey: 'nav.schedule', icon: 'event' },
    { to: '/curator/speaking-rooms', labelKey: 'nav.speakingRooms', icon: 'forum' },
    { to: '/curator/daily-tasks', labelKey: 'nav.dailyTasks', icon: 'fact_check' },
    { to: '/curator/trainer-schedule', labelKey: 'nav.trainerSchedule', icon: 'date_range' },
    { to: '/curator/cue-cards', labelKey: 'nav.cueCards', icon: 'style' },
    // ⚠️ Раздела историй у куратора НЕ БЫЛО ВОВСЕ (замечено заказчиком
    // 05.09.2026), хотя обращаться к своей группе нужно именно ему.
    { to: '/curator/stories', labelKey: 'nav.stories', icon: 'photo_camera' },
    { to: '/curator/materials', labelKey: 'nav.materials', icon: 'video_library' },
    { to: '/curator/profile', labelKey: 'nav.profile', icon: 'person' },
  ],
  admin: [
    { to: '/admin', labelKey: 'nav.dashboard', icon: 'home' },
    { to: '/admin/marathons', labelKey: 'nav.marathons', icon: 'school' },
    { to: '/admin/students', labelKey: 'nav.students', icon: 'groups' },
    // Массовые загрузки учеников: отчёт и таблица с паролями. Раздел нужен
    // потому, что таблица приходит НЕ в браузер: работу делает сервер.
    { to: '/admin/imports', labelKey: 'nav.imports', icon: 'upload_file' },
    { to: '/admin/groups', labelKey: 'nav.groups', icon: 'group' },
    // Корзина — шестым пунктом, вне нижнего меню телефона: действие редкое, а
    // первые пять пунктов — то, чем директор пользуется каждый день.
    { to: '/admin/trash', labelKey: 'nav.trash', icon: 'delete' },
    { to: '/admin/leads', labelKey: 'nav.leads', icon: 'send' },
    { to: '/admin/curators', labelKey: 'roles.curator', icon: 'co_present' },
    { to: '/admin/schedule', labelKey: 'nav.schedule', icon: 'event' },
    // ⚠️ Пункта здесь НЕ БЫЛО до 05.09.2026. Страница существовала и работала, но
    // добраться до неё мышкой было нельзя — только по прямому адресу. У куратора
    // пункт был, у администратора нет, и заказчик искал его именно в админке.
    { to: '/admin/speaking-rooms', labelKey: 'nav.speakingRooms', icon: 'forum' },
    { to: '/admin/homework', labelKey: 'nav.homework', icon: 'assignment' },
    { to: '/admin/flagged-works', labelKey: 'nav.flaggedWorks', icon: 'flag' },
    { to: '/admin/stories', labelKey: 'nav.stories', icon: 'photo_camera' },
    { to: '/admin/materials', labelKey: 'nav.materials', icon: 'video_library' },
    { to: '/admin/ielts-writing', labelKey: 'nav.ieltsWriting', icon: 'edit_note' },
    { to: '/admin/ielts-speaking', labelKey: 'nav.ieltsSpeaking', icon: 'record_voice_over' },
    { to: '/admin/ielts-reading', labelKey: 'nav.ieltsReading', icon: 'menu_book' },
    { to: '/admin/ielts-listening', labelKey: 'nav.ieltsListening', icon: 'headphones' },
    { to: '/admin/ielts-pool', labelKey: 'nav.ieltsPool', icon: 'database' },
    // Движок ИИ-заданий (D27): генерация, две проверки ИИ, очередь одобрения,
    // промпты. Значок из ПРОВЕРЕННОГО списка (iconSubset.test.ts).
    { to: '/admin/ai-pipeline', labelKey: 'nav.aiPipeline', icon: 'smart_toy' },
    // Тексты уведомлений (D45): матрица «событие × язык», правка без выката.
    // Значок из ПРОВЕРЕННОГО списка (iconSubset.test.ts): «campaign» в вырезанном
    // шрифте есть.
    { to: '/admin/notification-templates', labelKey: 'nav.notificationTemplates', icon: 'campaign' },
    // Тоже не было в меню администратора, хотя страница есть и у куратора пункт
    // имеется — нашлось сторожем `navReachable.test.ts` заодно со Speaking Rooms.
    { to: '/admin/cue-cards', labelKey: 'nav.cueCards', icon: 'style' },
    { to: '/admin/app-config', labelKey: 'nav.appConfig', icon: 'tune' },
    // Техническая страница: живы ли службы. Нужна ровно в тот момент, когда
    // «у ученика не работает» и надо понять — это у него или у нас.
    { to: '/admin/platform-health', labelKey: 'nav.platformHealth', icon: 'monitoring' },
    // Витрина правок интерфейса: посмотреть вживую до выката на учеников.
    // Значок из ПРОВЕРЕННОГО списка: «preview» в вырезанном шрифте НЕТ.
    { to: '/admin/preview', labelKey: 'nav.preview', icon: 'play_circle' },
    // Опись базы тренажёров: сколько заданий каждого вида и что из них в ходу.
    // Значок из ПРОВЕРЕННОГО списка (iconSubset.test.ts) — вырезанный шрифт
    // рисует отсутствующий знак его именем заглавными: «inventory» в нём НЕТ,
    // а «inventory_2» есть.
    { to: '/admin/trainer-pool', labelKey: 'nav.trainerPool', icon: 'inventory_2' },
    // Показатели сервера с историей: память, диск, процессор, сеть, журнал
    // служб. Значок из ПРОВЕРЕННОГО списка (iconSubset.test.ts).
    { to: '/admin/server', labelKey: 'nav.server', icon: 'monitoring' },
    // Падения приложения (T23). Значок из ПРОВЕРЕННОГО списка (iconsInFont.json).
    { to: '/admin/app-crashes', labelKey: 'nav.appCrashes', icon: 'error_outline' },
    // Значок берём из ПРОВЕРЕННОГО списка (см. iconSubset.test.ts): шрифт у нас
    // вырезанный, и отсутствующий знак рисует своё ИМЯ заглавными.
    { to: '/admin/devices', labelKey: 'nav.devices', icon: 'lock' },
    { to: '/admin/daily-tasks', labelKey: 'nav.dailyTasks', icon: 'fact_check' },
    { to: '/admin/trainer-schedule', labelKey: 'nav.trainerSchedule', icon: 'date_range' },
    { to: '/admin/stats', labelKey: 'nav.stats', icon: 'monitoring' },
    { to: '/admin/recordings', labelKey: 'nav.recordings', icon: 'smart_display' },
    { to: '/admin/chats', labelKey: 'nav.chat', icon: 'forum' },
  ],
};

/** Role label shown in the shell header — director vs manager are distinct. */
const ROLE_LABEL_KEY: Record<Role, string> = {
  admin: 'roles.director',
  manager: 'roles.manager',
  curator: 'roles.curator',
  student: 'roles.student',
};

export function AppShell({ role }: { role: ShellRole }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Edvibe-style rail: an icon-only sidebar pinned to the left edge that expands
  // to reveal labels on hover/focus, overlaying the content (which never shifts).
  const [expanded, setExpanded] = useState(false);
  const collapsed = !expanded;
  /** Кабинет ученика идёт по макету «Desktop Web v2»; экраны сотрудников — нет. */
  const sand = role === 'student';

  /**
   * Метка песочного вида вешается на КОРЕНЬ ДОКУМЕНТА, а не на свой блок.
   *
   * ⚠️ Сперва она стояла на блоке — и шрифт текста не менялся. Причина: имя
   * семейства задано переменной на `body`, а `body` лежит ВЫШЕ этого блока.
   * Значение переменной там вычисляется один раз, потомки наследуют уже
   * готовое имя, и подмена внутри до них не доходила. Заголовки менялись
   * (у них своё правило), а обычный текст оставался прежним — расхождение
   * видно только замером, глазом два похожих шрифта не различить.
   *
   * Заодно в песочный цвет красится сама страница: при оттяжке за край
   * прокрутки из-под кабинета выглядывал прежний голубовато-серый фон.
   */
  useEffect(() => {
    if (!sand) return;
    document.documentElement.classList.add('theme-sand');
    return () => document.documentElement.classList.remove('theme-sand');
  }, [sand]);
  const user = useAuthStore((s) => s.user);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const logout = useAuthStore((s) => s.logout);
  const setUser = useAuthStore((s) => s.setUser);

  // Цифра у «Уведомлений» — только в кабинете куратора: у ученика своя шапка со
  // своим колокольчиком, админке лента пока не нужна.
  const unread = useUnreadNotifications(role === 'curator');
  const badgeOf = (item: NavItem) => (item.badge === 'notifications' ? unread : undefined);

  // Пуши: разрешение и метка устройства — только в приложении и только ученику с куратором (сам решает).
  usePushSync();

  // Refresh the current user (incl. tariff `features`) on shell mount. The user
  // is persisted from login, but `features` are authoritative from /auth/me.
  // Failures are ignored — the store keeps the last-known user (lockout-safe).
  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [setUser]);

  // A manager reaches the admin shell but must never see financial pages.
  const canSeeFinancials = user?.role === 'admin';

  /** Закрыт ли пункт тарифом. */
  const isLocked = (item: (typeof NAV_BY_ROLE)[typeof role][number]) => {
    if (!item.feature) return false;
    const keys = Array.isArray(item.feature) ? item.feature : [item.feature];
    return !keys.some((k) => computeHasFeature(user, k));
  };

  /**
   * ⚠️ ПУНКТ БЕЗ ПРАВА ПРЯЧЕТСЯ ВСЕГДА — И НА САЙТЕ, И В ПРИЛОЖЕНИИ.
   *
   * До 14.09.2026 ученику, ждущему начала обучения, на САЙТЕ пункт показывался
   * с замком «Доступно ученикам академии». Решение владельца 14.09.2026
   * (Apple 3.1.3(f)): никаких замков нигде. Ученик только с бесплатным набором
   * тоже получает `awaiting_start = true` — и видел замки на «Чате» и
   * «Тренажёрах». Отказ Apple 30.08 пришёл как раз со снимком сайта.
   */
  /**
   * ⚠️ В ПРИЛОЖЕНИИ ПУНКТЫ УЧЕНИКА — ТОЛЬКО ВКЛАДКИ ИЗ РАЗДЕЛОВ С СЕРВЕРА (D75, 26.09.2026, `lib/storeTabs.ts`).
   * Расписания, чата и прочего вне меню приложения там нет ни внизу, ни сбоку (на широком экране планшета
   * меню боковое) — иначе на iPad ревьюер увидел бы раздел, которого на телефоне нет.
   */
  const appTabs = IS_APP_BUILD && role === 'student' ? storeTabs(user ? userSections(user) : undefined) : [];
  /**
   * ⚠️ ВКЛАДКА «ТРЕНАЖЁРЫ» БЕЗ ЕДИНОГО НАВЫКА ПРЯЧЕТСЯ (1.1.2, docs/CONTRACTS/ai-skills.md §1, `trainersTabEmpty`).
   * Ручку зовём, только если вкладка уже есть по разделам: бесплатнику ни одного запроса.
   *
   * ⚠️ НЕ ХУК `useAiSkills` С `false`. Тогда он зовёт загрузку с учёткой `null` и сбрасывает общее хранилище, а эффект
   * оболочки срабатывает ПОСЛЕ эффекта страницы внутри неё: на сайте оболочка выбрасывала бы ответ, который запросила
   * главная или хаб, и плитки тренажёров оставались бы закрытыми. Поэтому здесь — запрос только при вкладке и
   * подписка без сброса.
   */
  const trainersTab = appTabs.includes('/app/trainers');
  const userId = user?.id;
  useEffect(() => {
    if (trainersTab && userId) void ensureAiSkills(userId);
  }, [trainersTab, userId]);
  useSyncExternalStore(subscribeAiSkills, getAiSkillsVersion, getAiSkillsVersion);
  const hideTrainersTab = trainersTab && trainersTabEmpty(aiSkillsLoaded(), aiSkillsFailed(), getAiSkills());
  const roleNav =
    IS_APP_BUILD && role === 'student'
      ? appTabs
          .filter((to) => !(hideTrainersTab && to === '/app/trainers'))
          .map((to) => NAV_BY_ROLE.student.find((i) => i.to === to))
          .filter((i): i is NavItem => Boolean(i))
      : NAV_BY_ROLE[role];
  const items = roleNav.filter((item) => {
    if (!canSeeFinancials && FINANCIAL_NAV_PATHS.has(item.to)) return false;
    if (!isFeatureOn('speaking_peers') && SPEAKING_ROOM_NAV_PATHS.has(item.to)) return false;
    // Tariff gating: only affects students; staff pass via computeHasFeature.
    if (isLocked(item)) return false;
    return true;
  });

  /**
   * Что показывать в нижнем меню телефона.
   *
   * У ученика по макету от 23.08.2026 ровно четыре пункта: главная, обучение,
   * ЖИ-тренажёры, словарь. Расписание переехало на главную недельной строкой,
   * а чат и профиль открываются с главной — иконками в шапке. Пятая и шестая
   * иконки на узком экране превращали подписи в нечитаемую кашу.
   *
   * У персонала меню оставляем как было: их кабинеты этот макет не затрагивал,
   * и урезать им навигацию никто не просил.
   *
   * В приложении (D75, 26.09.2026) вкладки — все пункты из разделов с сервера, включая Профиль: новичку 4
   * (Главная, Обучение, Словарь, Профиль), платнику с тренажёрами 5. Чата в приложении нет.
   */
  const MOBILE_STUDENT_TABS = IS_APP_BUILD
    ? items.map((i) => i.to)
    : ['/app', '/app/lessons', '/app/trainers', '/app/vocabulary'];
  /**
   * ⚠️ У СОТРУДНИКА ВНИЗУ 4 ПУНКТА + «ЕЩЁ» (решение владельца 13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §2).
   * Прежде внизу стояли просто первые пять пунктов: боковое меню на телефоне скрыто, и куратору оставалось 5 разделов из 15, админу
   * 5 из 34, без профиля, языка и «Выйти». Остальные разделы — на экране «Ещё» (`pages/staff/StaffMorePage.tsx`),
   * состав — `lib/staffMore.ts`. Цифра уведомлений куратора — и на «Ещё»: сам пункт «Уведомления» теперь там.
   */
  const mobileItems: NavItem[] =
    role === 'student'
      ? MOBILE_STUDENT_TABS.map((to) => items.find((i) => i.to === to)).filter(
          (i): i is NavItem => Boolean(i),
        )
      : [
          ...splitStaffNav(role, items).dock,
          { to: MORE_PATH[role], labelKey: 'nav.more', icon: 'more_horiz', badge: role === 'curator' ? 'notifications' : undefined },
        ];

  // ── Аппаратная «Назад» на Android ───────────────────────────────────────────
  // Главный экран роли: у ученика «/app», у куратора «/curator», у админа
  // «/admin» — это первый пункт меню роли. Разделы меню возвращают сюда,
  // а отсюда кнопка предлагает выйти (решение заказчика — окном, а не
  // двойным нажатием, чтобы случайный жест от края ничего не закрывал).
  const [exitAsk, setExitAsk] = useState(false);
  const base = NAV_BY_ROLE[role][0].to;
  // Корни разделов. На телефоне у сотрудника корни — только пункты нижнего меню и «Ещё»: раздел, открытый из «Ещё»,
  // возвращается в «Ещё» обычным «назад» (кнопка Android, свайп iPhone), а не на главную роли.
  // Ключ строкой: массивы пунктов собираются заново на каждой отрисовке, а подписка на «назад» — нет.
  const isMobile = useIsMobile();
  const rootKey = (role !== 'student' && isMobile ? mobileItems : items).map((i) => i.to).join('|');
  const sectionPaths = useMemo(() => rootKey.split('|'), [rootKey]);
  const askExit = useCallback(() => setExitAsk(true), []);
  useAndroidBack(base, sectionPaths, askExit);
  // Свайп назад на iPhone: на корнях разделов его нет, как нет и выхода кнопкой Android.
  useSwipeBackRoots(sectionPaths);

  const confirmExit = () => {
    tapHeavy();
    void CapacitorApp.exitApp();
  };

  const handleLogout = async () => {
    // Метку устройства снимаем ДО сброса сессии и с ожиданием (lib/push.ts): на сайте — мгновенный выход.
    await disablePush();
    if (refreshToken) {
      // Fire-and-forget — even if the server is unreachable, drop local session.
      authApi.logout(refreshToken).catch(() => {});
    }
    // Вышел сам — без `?next` (lib/loginReturn.ts, 27.09.2026).
    markUserLogout();
    logout();
    navigate('/login', { replace: true });
  };

  return (
    /* ⚠️ `min-h-full`, а не `min-h-screen`. `100vh` — это высота ВСЕГО экрана,
       включая чёлку и полосу жеста, а видимая область меньше. Кабинет получался
       выше окна ровно на эту разницу, и страница прокручивалась даже когда
       содержимого на экран не набиралось: заказчик 23.08.2026 — «почему скролл
       заходит вверх бесконечно?». `100%` считается от body, а он равен видимой
       области. */
    // ⚠️ ПЕСОЧНАЯ ПАЛИТРА — ТОЛЬКО УЧЕНИКУ (макет «Desktop Web v2», 01.09.2026).
    //
    // На телефоне она и так включается по ширине экрана и накрывает всех.
    // Здесь — по роли: кураторка и админка остаются прежними. Там работают
    // сотрудники, и перекрашивать им привычные экраны ради макета ученика
    // незачем. Значения обоих списков сверяет `index.paletteInSync.test.ts`.
    <div className="min-h-full bg-background">
      <OfflineBar />
      <AudioNotice />
      {/* Полоска записи: видна при любой прокрутке, пока идёт запись голоса. */}
      <RecordingBar />
      {/* Спрашиваем про вход по лицу один раз после первого входа — в браузере
          окно не появляется вовсе. */}
      <BiometricPrompt />
      {/* Desktop rail (md+) — icon-only, pinned to the left edge. Expands on
          hover/focus to reveal labels, overlaying the content (which keeps its
          fixed left margin so it never reflows). Edvibe-style navigation.
          ⚠️ translate="no" — вторая страховка от переводчика браузера (техдолг 263):
          подписи меню остаются на языке интерфейса, язык меняется в самом приложении.
          Только на меню — урок переводить можно, ученики делают это сознательно. */}
      <aside
        translate="no"
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocusCapture={() => setExpanded(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setExpanded(false);
        }}
        className={cn(
          'safe-side hidden md:fixed md:inset-y-0 md:left-0 md:z-40 md:flex md:flex-col md:overflow-x-hidden md:transition-[width] md:duration-200',
          // Ученику — как в макете: песочная рельса, отделённая волоском.
          // Сотрудникам оставлена прежняя белая: их экраны мы не трогаем.
          sand
            ? 'md:border-r md:border-outline md:bg-surface-container'
            : 'md:bg-surface-container-lowest',
          collapsed
            ? 'md:w-[4.75rem] md:px-2 md:py-6'
            : cn('md:shadow-ambient', sand ? 'md:w-[15.25rem] md:p-6' : 'md:w-64 md:p-6'),
        )}
      >
        {/* Brand — a compact mark when collapsed, full wordmark on expand */}
        <div className={cn('flex items-center', collapsed && 'justify-center')}>
          {collapsed ? (
            <span className="grid h-9 w-9 place-items-center rounded-button font-display text-xl font-extrabold text-primary">
              {/* Знак «O» — и на сайте, и в приложении OneBilim Academy (ребрендинг 1.1.1; в D76 здесь было «1»). */}
              O
            </span>
          ) : (
            <div className="min-w-0">
              <h1 className="whitespace-nowrap font-display text-2xl font-extrabold tracking-display text-primary">
                {IS_APP_BUILD ? APP_BRAND : 'Onebilim'}
              </h1>
              <p className="mt-1 whitespace-nowrap text-xs uppercase tracking-label text-on-surface-variant">
                {t(ROLE_LABEL_KEY[user?.role ?? role])}
              </p>
            </div>
          )}
        </div>

        <nav className={cn('mt-8 min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden', collapsed && 'mt-6')}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/app' || item.to === '/curator' || item.to === '/admin'}
              title={collapsed ? t(item.labelKey) : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center font-semibold transition',
                  // Ученику — размеры макета: строка 48 точек, скругление 14,
                  // подпись 13,5 и ОБЯЗАТЕЛЬНО в одну строку: «AI Trainers»
                  // иначе разъезжается на две и строка растёт.
                  sand
                    ? 'h-12 whitespace-nowrap rounded-[14px] text-[13.5px]'
                    : 'rounded-button text-sm',
                  collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5',
                  isActive
                    ? 'bg-primary-fixed text-on-primary-fixed-variant'
                    : 'text-on-surface-variant hover:bg-surface-container-low',
                )
              }
            >
              <span className="relative inline-flex">
                <span className="material-symbols-outlined text-xl">{item.icon}</span>
                {collapsed && <NavBadge count={badgeOf(item)} className="absolute -right-2 -top-1.5" />}
              </span>
              {/* ⚠️ Подпись — ТОЛЬКО в своём <span>. Голый текст рядом с соседями, которые
                  появляются и исчезают, роняет весь кабинет у ученика с переводчиком
                  браузера: переводчик подменяет текстовый узел на <font>, меню сворачивается,
                  React удаляет свой узел, которого на месте уже нет, — «removeChild»
                  (жалоба 15.09.2026, техдолг 263). Элемент переводчик не трогает. */}
              {!collapsed && <span>{t(item.labelKey)}</span>}
              {!collapsed && <NavBadge count={badgeOf(item)} className="ml-auto" />}
            </NavLink>
          ))}
        </nav>

        <div className={cn('mt-auto', collapsed ? 'pt-4' : 'pt-6')}>
          {collapsed ? (
            <button
              onClick={handleLogout}
              title={t('common.logout')}
              aria-label={t('common.logout')}
              className="grid h-10 w-full place-items-center rounded-button text-on-surface-variant transition hover:bg-surface-container-low"
            >
              <span className="material-symbols-outlined text-xl">logout</span>
            </button>
          ) : (
            <>
              {/* Language switcher — TЗ requires KZ / RU / EN */}
              <div className="mb-3 flex items-center gap-1 rounded-button bg-surface-container-low p-1">
                {(['kk', 'ru', 'en'] as const).map((lng) => (
                  <button
                    key={lng}
                    type="button"
                    onClick={() => setLanguage(lng)}
                    className={cn(
                      'flex-1 rounded-button px-2 py-1 text-[11px] font-semibold uppercase tracking-label transition',
                      i18n.language === lng
                        ? 'bg-primary text-on-primary'
                        : 'text-on-surface-variant hover:bg-surface-container',
                    )}
                  >
                    {lng === 'kk' ? 'ҚАЗ' : lng === 'ru' ? 'РУС' : 'ENG'}
                  </button>
                ))}
              </div>
              {user && (
                <div className="mb-3 truncate text-xs text-on-surface-variant">
                  {user.full_name}
                  <br />
                  <span className="opacity-60">{user.email}</span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-button px-3 py-2 text-sm font-semibold text-on-surface-variant hover:bg-surface-container-low"
              >
                <span className="material-symbols-outlined text-xl">logout</span>
                {t('common.logout')}
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main content — offset by the icon rail's width and never reflows when
          the rail expands (the rail overlays it). Content is capped and centered
          in the remaining space so it doesn't sprawl on wide monitors. */}
      <main className="md:ml-[4.75rem]">
        <div
          className={cn(
            // Отступы живут в .safe-main (index.css): снизу место под
            // плавающую навигацию, а сверху и по бокам — под чёлку и вырез.
            'safe-main mx-auto',
            role === 'admin' ? 'max-w-7xl' : 'max-w-6xl',
          )}
        >
          {/*
            ⚠️ ПОЛОСЫ «ПОЧТА НЕ ПОДТВЕРЖДЕНА» БОЛЬШЕ НЕТ (04.09.2026, решение
            заказчика: «убери этот бейдж чтобы не появлялся ни у кого»).

            Она имела смысл при самостоятельной регистрации. Но школа заводит
            учеников сама и высылает пароль письмом — человек, читающий эту
            полосу, УЖЕ получил доступ по почте. Напоминание на каждом экране
            выглядело упрёком за то, чего он не делал.

            Подтверждение почты как таковое осталось: страница /verify-email
            работает, восстановление пароля по-прежнему её требует.
          */}
          <Outlet context={{ logout: handleLogout, navItems: items, unread: unread ?? 0 } satisfies ShellContext} />
        </div>
      </main>

      {/* ── Нижнее меню телефона ──────────────────────────────────────────
          Четыре пункта, а не пять: по макету от 23.08.2026 расписание уехало на
          главную недельной строкой — те же семь кружков показывают и серию, и
          занятия дня. Пятая иконка на узком экране съедала подписи, а расписание
          всё равно нужно видеть сразу, а не через вкладку.

          Экран расписания остаётся по своему адресу — на него ведёт строка с
          ближайшим занятием.

          Форма из макета: сплошная тонированная «таблетка» без стекла и тени,
          у активного пункта — тёплая подложка. */}
      {/* Меню — сплошная панель, прижатая к нижнему краю, со скруглённым верхом:
          решение заказчика 23.08.2026 («как в каспи»), а не плавающая таблетка.
          Разницу видно на длинных списках: панель отсекает содержимое ровной
          линией, и не остаётся ощущения, что под ней что-то потерялось.
          Отступ безопасной зоны держит сама панель — под ней ничего нет. */}
      <nav
        // Под панелью нативный ролик урока обрезается (ветка video-native-webview) — `lib/inlineVideoNative.ts`.
        data-native-video-cover=""
        className="app-dock fixed inset-x-0 bottom-0 z-30 rounded-t-[22px] bg-surface-container-lowest px-2 pt-1.5 shadow-[0_-4px_20px_rgba(36,31,27,0.10)] md:hidden"
        style={{ paddingBottom: 'max(0.5rem, var(--sa-bottom))' }}
      >
        <div className="flex items-center justify-around">
          {mobileItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/app' || item.to === '/curator' || item.to === '/admin'}
              // Лёгкая вибрация на вкладке — только в приложении (решение владельца 24.09.2026, `lib/haptics.ts`).
              onClick={tapSelection}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center justify-center gap-0.5 rounded-pill py-2 text-[9.5px] font-semibold leading-tight transition',
                  // «Ещё» горит и на разделах из его групп — иначе в нижнем меню не горело бы ничего (13.10.2026).
                  isActive || (role !== 'student' && item.to === MORE_PATH[role] && isUnderMore(role, location.pathname))
                    ? 'bg-primary-container text-on-primary-container'
                    : 'text-on-surface-variant',
                )
              }
            >
              <span className="relative inline-flex">
                <span className="material-symbols-outlined text-[21px]">{item.icon}</span>
                <NavBadge count={badgeOf(item)} className="absolute -right-2.5 -top-1" />
              </span>
              {/* Однословная подпись — в одну строку: «ИИ-тренажёры» на 390 переносилась по дефису (iOS 16.4, 28.10). */}
              <span className={cn(dockLabelNoWrap(t(item.labelKey)) && 'whitespace-nowrap')}>{t(item.labelKey)}</span>
            </NavLink>
          ))}
          {/* «Выйти» у сотрудника на телефоне — последняя строка экрана «Ещё» (13.10.2026). С 12.10 здесь стояла
              шестая кнопка «Выйти» (терминал 2, сборка 1.1.2); её сменил пункт «Ещё»: выход тот же, оболочки, и
              отдаётся экрану «Ещё» через `<Outlet context>` — одно место выхода. */}
        </div>
      </nav>

      {/* Выход по аппаратной «Назад» с главного экрана — только Android. */}
      <Modal open={exitAsk} onClose={() => setExitAsk(false)} title={t('app.exitTitle')} size="sm">
        <p className="text-sm text-on-surface-variant">{t('app.exitDesc')}</p>
        <div className="mt-5 flex gap-3">
          <Button variant="secondary" fullWidth onClick={() => setExitAsk(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" fullWidth onClick={confirmExit}>
            {t('app.exitConfirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
